import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import styles from './GuildDashboard.module.css';

interface Guild {
  id: number;
  name: string;
  tag: string;
  level: number;
  experience: number;
  experienceToNext: number;
  memberCount: number;
  maxMembers: number;
  emblem: {
    backgroundColor: string;
    iconType: string;
    iconColor: string;
    borderType: string;
    borderColor: string;
  };
  buildings: {
    trainingGroundLevel: number;
    libraryLevel: number;
    infirmaryLevel: number;
    warehouseLevel: number;
    sleepingHallLevel: number;
  };
  vault: {
    silver: number;
    capacity: number;
  };
}

interface GuildMember {
  id: number;
  username: string;
  memberRole: string;
  joinedAt: string;
  contributionPoints: number;
  lastActive: string;
}

const GuildDashboard: React.FC = () => {
  const { user } = useAuth();
  const [guild, setGuild] = useState<Guild | null>(null);
  const [members, setMembers] = useState<GuildMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchGuildData();
  }, []);

  const fetchGuildData = async () => {
    try {
      const response = await fetch('/api/guild/my-guild', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setGuild(data.guild);
        setMembers(data.members || []);
      } else if (response.status === 404) {
        // User is not in a guild
        setGuild(null);
      } else {
        setError('Failed to load guild data');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderEmblem = (emblem: Guild['emblem']) => {
    return (
      <div 
        className={styles.emblem}
        style={{
          backgroundColor: emblem.backgroundColor,
          borderColor: emblem.borderColor
        }}
      >
        <div 
          className={`${styles.emblemIcon} ${styles[`icon-${emblem.iconType}`]} ${styles[`border-${emblem.borderType}`]}`}
          style={{ color: emblem.iconColor }}
        >
          {emblem.iconType.charAt(0).toUpperCase()}
        </div>
      </div>
    );
  };

  const renderOverview = () => {
    if (!guild) return null;

    return (
      <div className={styles.overview}>
        <div className={styles.guildHeader}>
          {renderEmblem(guild.emblem)}
          <div className={styles.guildInfo}>
            <h2>[{guild.tag}] {guild.name}</h2>
            <div className={styles.guildStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Level:</span>
                <span className={styles.statValue}>{guild.level}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Members:</span>
                <span className={styles.statValue}>{guild.memberCount}/{guild.maxMembers}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Experience:</span>
                <span className={styles.statValue}>{guild.experience}/{guild.experienceToNext}</span>
              </div>
            </div>
            <div className={styles.experienceBar}>
              <div 
                className={styles.experienceProgress}
                style={{ width: `${(guild.experience / guild.experienceToNext) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className={styles.buildingsGrid}>
          <div className={styles.building}>
            <h4>Training Ground</h4>
            <p>Level {guild.buildings.trainingGroundLevel}</p>
            <small>Increases combat effectiveness</small>
          </div>
          <div className={styles.building}>
            <h4>Library</h4>
            <p>Level {guild.buildings.libraryLevel}</p>
            <small>Boosts experience gain</small>
          </div>
          <div className={styles.building}>
            <h4>Infirmary</h4>
            <p>Level {guild.buildings.infirmaryLevel}</p>
            <small>Reduces recovery time</small>
          </div>
          <div className={styles.building}>
            <h4>Warehouse</h4>
            <p>Level {guild.buildings.warehouseLevel}</p>
            <small>Increases vault capacity</small>
          </div>
          <div className={styles.building}>
            <h4>Sleeping Hall</h4>
            <p>Level {guild.buildings.sleepingHallLevel}</p>
            <small>Increases member limit</small>
          </div>
        </div>

        <div className={styles.vaultInfo}>
          <h3>Guild Vault</h3>
          <div className={styles.vaultStats}>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Silver:</span>
              <span className={styles.statValue}>{guild.vault.silver.toLocaleString()}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Capacity:</span>
              <span className={styles.statValue}>{guild.vault.capacity}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMembers = () => {
    return (
      <div className={styles.members}>
        <h3>Guild Members ({members.length})</h3>
        <div className={styles.membersList}>
          {members.map(member => (
            <div key={member.id} className={styles.memberCard}>
              <div className={styles.memberInfo}>
                <h4>{member.username}</h4>
                <span className={`${styles.role} ${styles[member.memberRole.toLowerCase()]}`}>
                  {member.memberRole}
                </span>
              </div>
              <div className={styles.memberStats}>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Contribution:</span>
                  <span className={styles.statValue}>{member.contributionPoints}</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Joined:</span>
                  <span className={styles.statValue}>
                    {new Date(member.joinedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Last Active:</span>
                  <span className={styles.statValue}>
                    {new Date(member.lastActive).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderVault = () => {
    return (
      <div className={styles.vault}>
        <h3>Guild Vault</h3>
        <p>Vault management interface coming soon...</p>
        <div className={styles.vaultActions}>
          <button className={styles.actionButton}>Deposit Items</button>
          <button className={styles.actionButton}>Withdraw Items</button>
          <button className={styles.actionButton}>View History</button>
        </div>
      </div>
    );
  };

  const renderLeague = () => {
    return (
      <div className={styles.league}>
        <h3>Guild League</h3>
        <p>League standings and match schedules coming soon...</p>
        <div className={styles.leagueInfo}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Current Season:</span>
            <span className={styles.statValue}>Winter 2024</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>League Position:</span>
            <span className={styles.statValue}>-</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Points:</span>
            <span className={styles.statValue}>0</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading guild data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  if (!guild) {
    return (
      <div className={styles.container}>
        <div className={styles.noGuild}>
          <h2>You are not in a guild</h2>
          <p>Create a guild or join an existing one to access guild features.</p>
          <button 
            className={styles.createButton}
            onClick={() => window.location.href = '/guild/create'}
          >
            Create Guild
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'members' ? styles.active : ''}`}
          onClick={() => setActiveTab('members')}
        >
          Members
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'vault' ? styles.active : ''}`}
          onClick={() => setActiveTab('vault')}
        >
          Vault
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'league' ? styles.active : ''}`}
          onClick={() => setActiveTab('league')}
        >
          League
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'members' && renderMembers()}
        {activeTab === 'vault' && renderVault()}
        {activeTab === 'league' && renderLeague()}
      </div>
    </div>
  );
};

export default GuildDashboard;
